function createWpIcon(name, className = '') {
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('class', `wp-icon wp-icon--${name}${className ? ` ${className}` : ''}`);
  icon.setAttribute('data-icon', name);
  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('focusable', 'false');
  const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
  use.setAttribute('href', `${document.documentElement.dataset.wpIconSprite || ''}#wp-icon-${name}`);
  icon.append(use);
  return icon;
}

class CopyToClipboard extends HTMLElement {
  constructor() {
    super();
    this.onCopy = this.copy.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('button, a');
    this.content = this.getAttribute('content');
    this.targetSelector = this.getAttribute('target');
    if (this.button) {
      this.button.addEventListener('click', this.onCopy);
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.onCopy);
    }
  }

  getContent() {
    if (this.content) return this.content;
    if (this.targetSelector) {
        const target = document.querySelector(this.targetSelector);
        return target ? target.textContent : '';
    }
    return '';
  }

  copy(event) {
    if (event) event.preventDefault();
    const textToCopy = this.getContent();
    if (!textToCopy) return;

    navigator.clipboard.writeText(textToCopy).then(() => {
        this.showSuccess();
    }).catch(err => {
        console.error('Failed to copy:', err);
    });
  }

  showSuccess() {
    this.setAttribute('status', 'success');
    const successText =
      this.dataset.successText || this.button?.dataset.successText || document.documentElement.dataset.copiedText || '';
    
    // Toggle icons if they exist with specific classes
    const copyIcon = this.button.querySelector('.wp-icon--copy');
    const checkIcon = this.button.querySelector('.wp-icon--check');
    const originalText = this.button.getAttribute('data-original-text') || this.button.innerHTML;
    
    // If we have specific icons, toggle them
    if (copyIcon && checkIcon) {
        copyIcon.style.display = 'none';
        checkIcon.style.display = 'inline-block';
    } 
    // Fallback: simple text toggle if used in code blocks maybe?
    else {
         // Save original if not saved
         if (!this.button.getAttribute('data-original-text')) {
             this.button.setAttribute('data-original-text', this.button.innerHTML);
         }
         // Simple HTML swap if no specific icon structure found
         if (!this.querySelector('.wp-icon--check') && successText) {
             const successIcon = createWpIcon('check');
             this.button.replaceChildren(successIcon, document.createTextNode(` ${successText}`));
         }
    }
    
    // Feedback element sibling?
    const feedback = this.querySelector('.copy-feedback') || this.nextElementSibling;
    if (feedback && feedback.classList.contains('discount-copy-feedback')) {
        feedback.textContent = successText;
    }


    setTimeout(() => {
        this.removeAttribute('status');
        if (copyIcon && checkIcon) {
            copyIcon.style.display = 'inline-block';
            checkIcon.style.display = 'none';
        } else if (this.button.getAttribute('data-original-text')) {
            this.button.innerHTML = this.button.getAttribute('data-original-text');
        }

        if (feedback && feedback.classList.contains('discount-copy-feedback')) {
            feedback.textContent = '';
        }
    }, 2000);
  }
}

customElements.define('copy-to-clipboard', CopyToClipboard);

class BeforeAfterSlider extends HTMLElement {
  constructor() {
    super();
    this.updatePosition = this.updatePosition.bind(this);
    this.startHandleDrag = this.startHandleDrag.bind(this);
    this.dragHandle = this.dragHandle.bind(this);
    this.stopHandleDrag = this.stopHandleDrag.bind(this);
  }

  connectedCallback() {
    this.range = this.querySelector('.before-after-slider__range');
    this.comparison = this.querySelector('.before-after-slider__comparison');
    this.divider = this.querySelector('.before-after-slider__divider');
    this.previewFields = this.querySelectorAll('[data-preview-value]');
    this.typingStart = Number(this.dataset.typingStart) || 30;
    this.typingComplete = Number(this.dataset.typingComplete) || 80;
    if (!this.range) return;

    this.updatePosition();
    this.range.addEventListener('input', this.updatePosition);
    this.divider?.addEventListener('pointerdown', this.startHandleDrag);
  }

  disconnectedCallback() {
    if (this.range) {
      this.range.removeEventListener('input', this.updatePosition);
    }
    this.divider?.removeEventListener('pointerdown', this.startHandleDrag);
    this.divider?.removeEventListener('pointermove', this.dragHandle);
    this.divider?.removeEventListener('pointerup', this.stopHandleDrag);
    this.divider?.removeEventListener('pointercancel', this.stopHandleDrag);
    window.clearInterval(this.typingTimer);
  }

  startHandleDrag(event) {
    if (event.button !== 0 || !this.range || !this.comparison) return;

    event.preventDefault();
    this.divider.setPointerCapture(event.pointerId);
    this.divider.addEventListener('pointermove', this.dragHandle);
    this.divider.addEventListener('pointerup', this.stopHandleDrag);
    this.divider.addEventListener('pointercancel', this.stopHandleDrag);
    this.setRangeFromPointer(event.clientX);
  }

  dragHandle(event) {
    this.setRangeFromPointer(event.clientX);
  }

  stopHandleDrag(event) {
    if (this.divider.hasPointerCapture(event.pointerId)) {
      this.divider.releasePointerCapture(event.pointerId);
    }
    this.divider.removeEventListener('pointermove', this.dragHandle);
    this.divider.removeEventListener('pointerup', this.stopHandleDrag);
    this.divider.removeEventListener('pointercancel', this.stopHandleDrag);
  }

  setRangeFromPointer(clientX) {
    const bounds = this.comparison.getBoundingClientRect();
    const value = Math.round(Math.min(100, Math.max(0, ((clientX - bounds.left) / bounds.width) * 100)));
    this.range.value = value;
    this.range.dispatchEvent(new Event('input', { bubbles: true }));
  }

  updatePosition() {
    const position = Number(this.range.value);
    this.style.setProperty('--before-after-position', `${position}%`);
    this.updatePreview(position);
  }

  updatePreview(position) {
    const progress = Math.min(1, Math.max(0, (position - this.typingStart) / (this.typingComplete - this.typingStart)));
    window.clearInterval(this.typingTimer);

    if (progress === 1 && this.lastPreviewProgress < 1) {
      this.animatePreview(Math.floor(this.getTotalPreviewLength() * (this.lastPreviewProgress || 0)));
    } else {
      this.renderPreview(Math.floor(this.getTotalPreviewLength() * progress));
    }

    this.lastPreviewProgress = progress;
  }

  getTotalPreviewLength() {
    return [...this.previewFields].reduce((total, field) => total + (field.dataset.previewValue || '').length, 0);
  }

  renderPreview(characters) {
    let remaining = characters;
    this.previewFields.forEach((field) => {
      const value = field.dataset.previewValue || '';
      field.textContent = value.slice(0, Math.max(0, Math.min(value.length, remaining)));
      remaining -= value.length;
    });
  }

  animatePreview(startLength) {
    let length = startLength;
    const totalLength = this.getTotalPreviewLength();

    this.typingTimer = window.setInterval(() => {
      length += 1;
      this.renderPreview(length);
      if (length >= totalLength) window.clearInterval(this.typingTimer);
    }, 28);
  }
}

if (!customElements.get('before-after-slider')) {
  customElements.define('before-after-slider', BeforeAfterSlider);
}

/* 
  Hydrate Legacy Code Blocks 
  Wraps existing code block logic to use the new standard if possible, 
  or simply provides the functionality using the same robust logic.
*/
document.addEventListener('DOMContentLoaded', () => {
    const codeBlocks = document.querySelectorAll('.custom-code-block');
    codeBlocks.forEach(block => {
        const copyButton = block.querySelector('.copy-code-button');
        const codeElement = block.querySelector('pre code');
        
        if (copyButton && codeElement && !copyButton.closest('copy-to-clipboard')) {
            copyButton.addEventListener('click', () => {
                navigator.clipboard.writeText(codeElement.innerText).then(() => {
                    const originalHTML = copyButton.innerHTML;
                    const copiedText =
                      copyButton.dataset.copiedText || document.documentElement.dataset.copiedText || '';
                    if (copiedText) {
                      const successIcon = createWpIcon('check');
                      copyButton.replaceChildren(successIcon, document.createTextNode(` ${copiedText}`));
                    }
                    setTimeout(() => {
                        copyButton.innerHTML = originalHTML;
                    }, 2000);
                });
            });
        }
    });
});

(function () {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  function findWidgets() {
    const selectors = [
      '#shopify-chat', 'shopify-chat', 'iframe[id*="chat-widget"]', '[id*="ShopifyChat"]'
    ];
    return document.querySelectorAll(selectors.join(','));
  }

  const observer = new MutationObserver(() => {
    const widgets = findWidgets();
    if (!widgets.length) return;
    const isOpen = drawer.classList.contains('active');
    widgets.forEach(widget => {
      widget.style.setProperty('visibility', isOpen ? 'hidden' : 'visible', 'important');
      widget.style.setProperty('pointer-events', isOpen ? 'none' : 'auto', 'important');
    });
  });

  observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
})();

class DigitalContentConsent extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedToConsent) return;

    this.isConnectedToConsent = true;
    this.checkbox = this.querySelector('.digital-content-consent__checkbox');
    this.status = this.querySelector('.digital-content-consent__status');
    this.form = document.getElementById(this.dataset.formId);
    this.scope = this.closest('.cart__blocks, .drawer__footer') || this.parentElement;
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.invalidateConsent = this.invalidateConsent.bind(this);
    this.refreshCheckoutState = this.refreshCheckoutState.bind(this);

    this.checkbox?.addEventListener('change', this.handleChange);
    this.form?.addEventListener('submit', this.handleSubmit);
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.invalidateConsent);
    this.checkoutObserver = new MutationObserver(this.refreshCheckoutState);
    if (this.scope) this.checkoutObserver.observe(this.scope, { childList: true, subtree: true });
    this.refreshCheckoutState();
  }

  disconnectedCallback() {
    this.checkbox?.removeEventListener('change', this.handleChange);
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.quantityUpdateUnsubscriber?.();
    this.checkoutObserver?.disconnect();
    this.isConnectedToConsent = false;
  }

  get hasPersistedConsent() {
    return this.checkbox?.checked && this.dataset.persisted === 'true';
  }

  refreshCheckoutState() {
    if (!this.scope) return;

    const checkoutIsAvailable = this.hasPersistedConsent && this.getAttribute('aria-busy') !== 'true';
    this.scope.querySelectorAll('button[name="checkout"]').forEach((button) => {
      button.disabled = !checkoutIsAvailable;
      button.setAttribute('aria-disabled', String(!checkoutIsAvailable));
    });
    this.scope.querySelectorAll('.additional-checkout-buttons').forEach((container) => {
      container.hidden = !checkoutIsAvailable;
      container.setAttribute('aria-hidden', String(!checkoutIsAvailable));
    });
  }

  handleSubmit(event) {
    if (this.hasPersistedConsent) return;

    event.preventDefault();
    this.status.textContent = this.dataset.requiredMessage;
    this.checkbox?.focus();
  }

  invalidateConsent() {
    this.checkbox.checked = false;
    this.dataset.persisted = 'false';
    this.querySelectorAll('[data-consent-attribute]').forEach((input) => {
      input.value = '';
    });
    this.refreshCheckoutState();
  }

  async handleChange() {
    const accepted = Boolean(this.checkbox?.checked);
    const timestamp = accepted ? new Date().toISOString() : '';
    this.setAttribute('aria-busy', 'true');
    this.status.textContent = '';
    this.refreshCheckoutState();

    const attributes = {
      'EU digital content consent': accepted ? 'Accepted' : '',
      'EU digital content consent version': accepted ? this.dataset.consentVersion : '',
      'EU digital content consent wording': accepted ? this.dataset.consentWording : '',
      'EU digital content consent cart': accepted ? this.dataset.cartSignature : '',
      'EU digital content consent timestamp': timestamp,
      'EU digital content consent country': accepted ? this.dataset.countryCode : '',
      'EU digital content consent language': accepted ? this.dataset.consentLanguage : '',
    };

    try {
      const response = await fetch(this.dataset.cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error('Consent update failed');

      document.querySelectorAll('digital-content-consent').forEach((component) => {
        if (component.dataset.cartSignature === this.dataset.cartSignature) {
          component.setPersistedState(accepted, timestamp);
        }
      });
    } catch (error) {
      this.checkbox.checked = false;
      this.dataset.persisted = 'false';
      this.status.textContent = this.dataset.errorMessage;
    } finally {
      this.removeAttribute('aria-busy');
      this.refreshCheckoutState();
    }
  }

  setPersistedState(accepted, timestamp) {
    this.checkbox.checked = accepted;
    this.dataset.persisted = String(accepted);
    const values = {
      accepted: accepted ? 'Accepted' : '',
      version: accepted ? this.dataset.consentVersion : '',
      wording: accepted ? this.dataset.consentWording : '',
      cart: accepted ? this.dataset.cartSignature : '',
      timestamp: accepted ? timestamp : '',
      country: accepted ? this.dataset.countryCode : '',
      language: accepted ? this.dataset.consentLanguage : '',
    };

    Object.entries(values).forEach(([key, value]) => {
      const input = this.querySelector(`[data-consent-attribute="${key}"]`);
      if (input) input.value = value;
    });
    this.status.textContent = '';
    this.refreshCheckoutState();
  }
}

if (!customElements.get('digital-content-consent')) {
  customElements.define('digital-content-consent', DigitalContentConsent);
}

class DigitalContentConsentClear extends HTMLElement {
  connectedCallback() {
    if (this.dataset.hasStaleConsent !== 'true') return;

    this.scope = this.closest('.cart__blocks, .drawer__footer') || this.parentElement;
    this.setCheckoutPending(true);
    DigitalContentConsentClear.pendingRequest ??= fetch(this.dataset.cartUpdateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          'EU digital content consent': '',
          'EU digital content consent version': '',
          'EU digital content consent wording': '',
          'EU digital content consent cart': '',
          'EU digital content consent timestamp': '',
          'EU digital content consent country': '',
          'EU digital content consent language': '',
        },
      }),
    }).then((response) => {
      if (!response.ok) throw new Error('Stale consent cleanup failed');
      return response;
    });

    DigitalContentConsentClear.pendingRequest
      .then(() => {
        this.dataset.hasStaleConsent = 'false';
        this.setCheckoutPending(false);
      })
      .catch(() => {
        this.setCheckoutPending(true);
      });
  }

  setCheckoutPending(isPending) {
    if (!this.scope) return;
    this.scope.querySelectorAll('button[name="checkout"]').forEach((button) => {
      button.disabled = isPending;
      button.setAttribute('aria-disabled', String(isPending));
    });
    this.scope.querySelectorAll('.additional-checkout-buttons').forEach((container) => {
      container.hidden = isPending;
      container.setAttribute('aria-hidden', String(isPending));
    });
  }
}

if (!customElements.get('digital-content-consent-clear')) {
  customElements.define('digital-content-consent-clear', DigitalContentConsentClear);
}

class PrivacyEmbed extends HTMLElement {
  connectedCallback() {
    this.loadButton = this.querySelector('[data-privacy-embed-load]');
    this.handleLoad = this.load.bind(this);
    this.loadButton?.addEventListener('click', this.handleLoad);
  }

  disconnectedCallback() {
    this.loadButton?.removeEventListener('click', this.handleLoad);
  }

  load() {
    if (!this.dataset.src) return;

    const iframe = document.createElement('iframe');
    iframe.src = this.dataset.src;
    iframe.title = this.dataset.title || 'Third-party content';
    iframe.width = '100%';
    iframe.height = this.dataset.height || '500';
    iframe.loading = 'lazy';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.setAttribute('frameborder', '0');

    if (this.dataset.allow) {
      iframe.setAttribute('allow', this.dataset.allow);
    }

    this.replaceChildren(iframe);
  }
}

if (!customElements.get('privacy-embed')) {
  customElements.define('privacy-embed', PrivacyEmbed);
}

class ProductMediaNavigation extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedToGallery) return;

    this.gallery = this.closest('media-gallery');
    this.previousButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    if (!this.gallery || !this.previousButton || !this.nextButton) return;

    this.isConnectedToGallery = true;
    this.showPrevious = () => this.changeMedia(-1);
    this.showNext = () => this.changeMedia(1);
    this.refreshState = this.updateState.bind(this);
    this.previousButton.addEventListener('click', this.showPrevious);
    this.nextButton.addEventListener('click', this.showNext);
    this.observer = new MutationObserver(this.refreshState);
    this.observer.observe(this.gallery, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });
    this.updateState();
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.showPrevious);
    this.nextButton?.removeEventListener('click', this.showNext);
    this.observer?.disconnect();
    this.isConnectedToGallery = false;
  }

  get mediaItems() {
    return Array.from(this.gallery.querySelectorAll('[id^="Slider-Gallery-"] > [data-media-id]'));
  }

  get activeIndex() {
    const items = this.mediaItems;
    const index = items.findIndex((item) => item.classList.contains('is-active'));
    return index < 0 ? 0 : index;
  }

  changeMedia(direction) {
    const items = this.mediaItems;
    const targetIndex = Math.min(items.length - 1, Math.max(0, this.activeIndex + direction));
    const target = items[targetIndex];
    if (!target || targetIndex === this.activeIndex) return;

    this.gallery.setActiveMedia(target.dataset.mediaId, false);
    this.updateState();
  }

  updateState() {
    const items = this.mediaItems;
    const index = this.activeIndex;
    const disablePrevious = items.length < 2 || index === 0;
    const disableNext = items.length < 2 || index === items.length - 1;
    this.previousButton.toggleAttribute('disabled', disablePrevious);
    this.nextButton.toggleAttribute('disabled', disableNext);
    this.previousButton.setAttribute('aria-disabled', String(disablePrevious));
    this.nextButton.setAttribute('aria-disabled', String(disableNext));
  }
}

if (!customElements.get('product-media-navigation')) {
  customElements.define('product-media-navigation', ProductMediaNavigation);
}

class CollectionLazyImages extends HTMLElement {
  connectedCallback() {
    this.images = Array.from(this.querySelectorAll('[data-collection-deferred-image]'));
    if (!this.images.length) return;

    if (!('IntersectionObserver' in window)) {
      this.images.forEach((image) => this.load(image));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.load(entry.target);
          this.observer.unobserve(entry.target);
        });
      },
      { rootMargin: '100px 0px' }
    );
    this.images.forEach((image) => this.observer.observe(image));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  load(image) {
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    if (image.dataset.src) image.src = image.dataset.src;
    image.removeAttribute('data-collection-deferred-image');
  }
}

if (!customElements.get('collection-lazy-images')) {
  customElements.define('collection-lazy-images', CollectionLazyImages);
}

(() => {
  const mode = document.documentElement.dataset.cardAnimation || 'smart';
  if (mode === 'animated') return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopHover = window.matchMedia('(min-width: 990px) and (hover: hover)');
  const connection = navigator.connection;
  const constrainedNetwork = () =>
    Boolean(connection && (connection.saveData || /(^|-)2g/.test(connection.effectiveType || '')));
  const selector = 'video[data-card-animation-video], img[data-card-animation-src]';
  const maxConcurrentLoads = 3;
  const extraVisibleLoads = 2;
  const videoSlotTimeout = 4000;
  const wanted = new Set();
  const inView = new Set();
  let active = 0;

  const isVideo = (element) => element.tagName === 'VIDEO';

  const load = (element, priority = 'low') => {
    if (element.dataset.cardAnimationState) return;
    const media = element.parentElement;
    element.dataset.cardAnimationState = 'loading';
    wanted.delete(element);
    media?.setAttribute('data-animation-loading', '');
    active += 1;
    let slotHeld = true;

    const releaseSlot = () => {
      if (!slotHeld) return;
      slotHeld = false;
      active -= 1;
      pump();
    };

    const finish = (state) => {
      if (element.dataset.cardAnimationState !== 'loading') return;
      element.dataset.cardAnimationState = state;
      media?.removeAttribute('data-animation-loading');
      if (state === 'loaded' && !isVideo(element)) element.classList.add('is-loaded');
      if (state === 'error' && isVideo(element)) useAnimatedImage(element);
      releaseSlot();
    };

    if (isVideo(element)) {
      ['loadeddata', 'canplay', 'playing'].forEach((type) =>
        element.addEventListener(type, () => finish('loaded'), { once: true })
      );
      element.addEventListener('error', () => finish('error'), { once: true });
      element.muted = true;
      element.preload = 'auto';
      element.src = element.dataset.cardAnimationVideo;
      setTimeout(releaseSlot, videoSlotTimeout);
      if (inView.has(element)) play(element);
    } else {
      element.addEventListener('load', () => finish('loaded'), { once: true });
      element.addEventListener('error', () => finish('error'), { once: true });
      element.fetchPriority = priority;
      if (element.dataset.cardAnimationSrcset) element.srcset = element.dataset.cardAnimationSrcset;
      element.src = element.dataset.cardAnimationSrc;
    }
  };

  const firstUnloaded = (elements) => {
    for (const element of elements) if (!element.dataset.cardAnimationState) return element;
    return null;
  };

  const pump = () => {
    let element = firstUnloaded(inView);
    while (element && active < maxConcurrentLoads + extraVisibleLoads) {
      load(element, 'high');
      element = firstUnloaded(inView);
    }
    element = firstUnloaded(wanted);
    while (element && active < maxConcurrentLoads) {
      load(element);
      element = firstUnloaded(wanted);
    }
  };

  const play = (video) => {
    if (!isVideo(video) || document.hidden || !video.isConnected) return;
    const state = video.dataset.cardAnimationState;
    if (!state) {
      pump();
      return;
    }
    if (state === 'error') return;
    video.play()?.catch((error) => {
      if (error && error.name === 'NotAllowedError') useAnimatedImage(video);
    });
  };

  const useAnimatedImage = (video) => {
    if (!video.isConnected || !video.dataset.cardAnimationFallbackSrc) return;
    const image = document.createElement('img');
    image.className = 'motion-reduce card__animation';
    image.alt = '';
    image.decoding = 'async';
    image.width = video.width;
    image.height = video.height;
    if (video.dataset.cardAnimationFallbackSizes) image.sizes = video.dataset.cardAnimationFallbackSizes;
    image.dataset.cardAnimationSrc = video.dataset.cardAnimationFallbackSrc;
    if (video.dataset.cardAnimationFallbackSrcset) {
      image.dataset.cardAnimationSrcset = video.dataset.cardAnimationFallbackSrcset;
    }
    inView.delete(video);
    video.parentElement?.removeAttribute('data-animation-loading');
    video.replaceWith(image);
    load(image, 'high');
  };

  const selfPaused = new WeakSet();

  const pause = (video) => {
    if (!isVideo(video) || video.paused) return;
    selfPaused.add(video);
    video.pause();
  };

  document.addEventListener(
    'pause',
    (event) => {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement) || !video.matches('video[data-card-animation-video]')) return;
      if (selfPaused.delete(video)) return;
      if (inView.has(video)) requestAnimationFrame(() => play(video));
    },
    true
  );

  document.addEventListener(
    'playing',
    (event) => {
      if (event.target instanceof HTMLVideoElement && event.target.matches('video[data-card-animation-video]')) {
        event.target.classList.add('is-playing');
      }
    },
    true
  );

  document.addEventListener('visibilitychange', () => {
    inView.forEach((element) => (document.hidden ? pause(element) : play(element)));
  });

  const whenIdle = (callback) =>
    'requestIdleCallback' in window ? requestIdleCallback(callback, { timeout: 1500 }) : setTimeout(callback, 600);
  const afterLoad = (callback) => {
    if (document.readyState === 'complete') whenIdle(callback);
    else window.addEventListener('load', () => whenIdle(callback), { once: true });
  };

  if (mode === 'smart') {
    if (reducedMotion.matches || constrainedNetwork() || !('IntersectionObserver' in window)) return;

    const nearObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !entry.target.dataset.cardAnimationState) wanted.add(entry.target);
          else wanted.delete(entry.target);
        });
        pump();
      },
      { rootMargin: '400px 0px' }
    );

    const viewObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            inView.add(entry.target);
            play(entry.target);
          } else {
            inView.delete(entry.target);
            pause(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const observe = (root) => {
      root.querySelectorAll(selector).forEach((element) => {
        if (element.dataset.cardAnimationObserved) return;
        element.dataset.cardAnimationObserved = 'true';
        nearObserver.observe(element);
        viewObserver.observe(element);
      });
    };

    afterLoad(() => {
      observe(document);
      new MutationObserver((mutations) => {
        mutations.forEach((mutation) =>
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element) observe(node);
          })
        );
      }).observe(document.body, { childList: true, subtree: true });
    });
    return;
  }

  const canHover = () => desktopHover.matches && !reducedMotion.matches;

  afterLoad(() => {
    if (!canHover() || constrainedNetwork() || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !entry.target.dataset.cardAnimationState) wanted.add(entry.target);
          else wanted.delete(entry.target);
        });
        pump();
      },
      { rootMargin: '200px 0px' }
    );
    document.querySelectorAll(selector).forEach((element) => observer.observe(element));
  });

  document.addEventListener(
    'pointerover',
    (event) => {
      if (event.pointerType !== 'mouse' || !canHover()) return;
      const card = event.target instanceof Element ? event.target.closest('.card-wrapper') : null;
      const element = card?.querySelector(selector);
      if (!element || inView.has(element)) return;
      inView.add(element);
      if (isVideo(element)) play(element);
      else load(element, 'high');
    },
    { passive: true }
  );

  document.addEventListener(
    'pointerout',
    (event) => {
      if (event.pointerType !== 'mouse') return;
      const card = event.target instanceof Element ? event.target.closest('.card-wrapper') : null;
      if (!card || (event.relatedTarget instanceof Node && card.contains(event.relatedTarget))) return;
      const element = card.querySelector(selector);
      if (!element) return;
      inView.delete(element);
      pause(element);
    },
    { passive: true }
  );
})();

class ProductMediaAnimation extends HTMLElement {
  connectedCallback() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (this.reducedMotion.matches || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(this.onIntersect.bind(this), { threshold: 0.2 });
    const containers = [...this.querySelectorAll('.product__media[data-animated-src]')];
    const first = containers.find((container) => container.closest('.is-active')) || containers[0];
    const rest = containers.filter((container) => container !== first);
    rest.forEach((container) => this.observer.observe(container));
    this.onIntent = this.onIntent.bind(this);
    this.addEventListener('pointerover', this.onIntent, { passive: true });
    this.addEventListener('click', this.onIntent, { passive: true });
    const warmRest = () => this.warm(rest);
    if (first) this.load(first, 'high').then(warmRest);
    else warmRest();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    this.removeEventListener('pointerover', this.onIntent);
    this.removeEventListener('click', this.onIntent);
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      this.observer.unobserve(entry.target);
      this.load(entry.target, 'high');
    });
  }

  onIntent(event) {
    const thumbnail = event.target instanceof Element ? event.target.closest('[data-target]') : null;
    if (!thumbnail) return;
    const media = this.querySelector(`[data-media-id="${thumbnail.dataset.target}"]`);
    const container = media?.querySelector('.product__media[data-animated-src]');
    if (container) this.load(container, 'high');
    media?.querySelector('animation-loop')?.load();
  }

  warm(containers) {
    const connection = navigator.connection;
    if (connection && (connection.saveData || /(^|-)2g/.test(connection.effectiveType || ''))) return;
    const next = () => {
      const container = containers.find((item) => item.dataset.animatedSrc);
      if (container) this.load(container, 'low').then(next);
    };
    if ('requestIdleCallback' in window) requestIdleCallback(next, { timeout: 1500 });
    else setTimeout(next, 1500);
  }

  load(container, priority) {
    const image = container.querySelector('img');
    if (!image || !container.dataset.animatedSrc) return container.animationLoad || Promise.resolve();
    const animated = new Image();
    animated.fetchPriority = priority;
    animated.sizes = image.sizes;
    animated.srcset = container.dataset.animatedSrcset;
    animated.src = container.dataset.animatedSrc;
    container.removeAttribute('data-animated-src');
    container.setAttribute('data-animation-loading', '');
    container.animationLoad = animated.decode().then(
      () => {
        image.srcset = animated.srcset;
        image.src = animated.src;
        container.removeAttribute('data-animation-loading');
      },
      () => container.removeAttribute('data-animation-loading')
    );
    return container.animationLoad;
  }
}

if (!customElements.get('product-media-animation')) {
  customElements.define('product-media-animation', ProductMediaAnimation);
}

class AnimationLoop extends HTMLElement {
  static inView = new Set();

  static observe(loop) {
    if (!AnimationLoop.nearObserver) {
      AnimationLoop.nearObserver = new IntersectionObserver(
        (entries) => entries.forEach((entry) => entry.isIntersecting && entry.target.load()),
        { rootMargin: '300px' }
      );
      AnimationLoop.viewObserver = new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              AnimationLoop.inView.add(entry.target);
              entry.target.play();
              entry.target.warmNeighbours();
            } else {
              AnimationLoop.inView.delete(entry.target);
              entry.target.pause();
            }
          }),
        { threshold: 0.2 }
      );
      document.addEventListener('visibilitychange', () =>
        AnimationLoop.inView.forEach((item) => (document.hidden ? item.pause() : item.play()))
      );
    }
    AnimationLoop.nearObserver.observe(loop);
    AnimationLoop.viewObserver.observe(loop);
  }

  connectedCallback() {
    this.video = this.querySelector('video');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.video || reducedMotion || !('IntersectionObserver' in window)) return;
    AnimationLoop.observe(this);
  }

  disconnectedCallback() {
    AnimationLoop.nearObserver?.unobserve(this);
    AnimationLoop.viewObserver?.unobserve(this);
    AnimationLoop.inView.delete(this);
  }

  load() {
    if (!this.video || this.started || this.waiting) return;
    const poster = this.parentElement?.querySelector('img');
    if (poster && !poster.complete) {
      this.waiting = true;
      const resume = () => {
        this.waiting = false;
        this.load();
      };
      poster.addEventListener('load', resume, { once: true });
      poster.addEventListener('error', resume, { once: true });
      return;
    }
    this.started = true;
    const holder = this.parentElement;
    const settle = () => holder?.removeAttribute('data-animation-loading');
    holder?.setAttribute('data-animation-loading', '');
    this.video.addEventListener('playing', () => {
      this.classList.add('is-playing');
      settle();
    });
    this.video.addEventListener('pause', () => {
      if (this.pausing) {
        this.pausing = false;
        return;
      }
      if (AnimationLoop.inView.has(this)) requestAnimationFrame(() => this.play());
    });
    this.video.addEventListener('error', () => this.useAnimatedImage(), { once: true });
    this.video.addEventListener('loadeddata', settle, { once: true });
    this.video.muted = true;
    this.video.preload = 'auto';
    this.video.src = this.dataset.src;
    if (AnimationLoop.inView.has(this)) this.play();
  }

  useAnimatedImage() {
    if (this.fellBack) return;
    this.fellBack = true;
    const holder = this.parentElement;
    const poster = holder?.querySelector('img');
    const fallbackSrc = this.dataset.fallbackSrc;
    const fallbackSrcset = this.dataset.fallbackSrcset;
    this.remove();
    if (!holder) return;
    if (!poster || !fallbackSrc) {
      holder.removeAttribute('data-animation-loading');
      return;
    }
    holder.setAttribute('data-animation-loading', '');
    const animated = new Image();
    animated.sizes = poster.sizes;
    if (fallbackSrcset) animated.srcset = fallbackSrcset;
    animated.src = fallbackSrc;
    animated
      .decode()
      .then(() => {
        poster.srcset = animated.srcset;
        poster.src = animated.src;
      })
      .catch(() => {})
      .finally(() => holder.removeAttribute('data-animation-loading'));
  }

  warmNeighbours() {
    const connection = navigator.connection;
    if (connection && (connection.saveData || /(^|-)2g/.test(connection.effectiveType || ''))) return;
    const slide = this.closest('li');
    ['previousElementSibling', 'nextElementSibling'].forEach((direction) => {
      let item = slide?.[direction];
      for (let step = 0; item && step < 3; step++, item = item[direction]) {
        const loop = item.querySelector('animation-loop');
        if (loop) return loop.load();
      }
    });
  }

  play() {
    if (!this.video || document.hidden || this.fellBack) return;
    if (!this.started) {
      this.load();
      return;
    }
    this.video.play()?.catch((error) => {
      if (error && error.name === 'NotAllowedError') this.useAnimatedImage();
    });
  }

  pause() {
    if (!this.video || this.video.paused) return;
    this.pausing = true;
    this.video.pause();
  }
}

if (!customElements.get('animation-loop')) {
  customElements.define('animation-loop', AnimationLoop);
}

class ShareThisButtons extends HTMLElement {
  connectedCallback() {
    if (!('IntersectionObserver' in window)) {
      ShareThisButtons.load(this.dataset.src);
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        ShareThisButtons.load(this.dataset.src);
      },
      { rootMargin: '300px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  static load(src) {
    if (!src || ShareThisButtons.loaded) return;
    ShareThisButtons.loaded = true;
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    document.head.append(script);
  }
}

if (!customElements.get('share-this-buttons')) {
  customElements.define('share-this-buttons', ShareThisButtons);
}

class DeferredContent extends HTMLElement {
  connectedCallback() {
    this.template = this.querySelector(':scope > template');
    if (!this.template) return;

    if (!('IntersectionObserver' in window)) {
      this.reveal();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.reveal();
      },
      { rootMargin: '300px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  reveal() {
    this.observer?.disconnect();
    if (!this.template?.isConnected) return;
    this.template.replaceWith(document.importNode(this.template.content, true));
  }
}

if (!customElements.get('deferred-content')) {
  customElements.define('deferred-content', DeferredContent);
}

class ArticleToc extends HTMLElement {
  constructor() {
    super();
    this.handleIntersect = this.handleIntersect.bind(this);
  }

  connectedCallback() {
    this.details = this.querySelector('details');
    this.links = [...this.querySelectorAll('a[href^="#"]')];
    this.targets = this.links
      .map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))))
      .filter(Boolean);

    this.links.forEach((link) => {
      const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (!target || target.closest('.blog-highlight-box')) link.closest('li')?.remove();
    });

    if (this.details && window.matchMedia('(max-width: 749px)').matches) {
      this.details.removeAttribute('open');
    }

    if (!('IntersectionObserver' in window) || !this.targets.length) return;
    this.visible = new Set();
    this.observer = new IntersectionObserver(this.handleIntersect, { rootMargin: '-15% 0px -70% 0px' });
    this.targets.forEach((target) => this.observer.observe(target));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) this.visible.add(entry.target.id);
      else this.visible.delete(entry.target.id);
    });
    const current = this.targets.find((target) => this.visible.has(target.id));
    if (!current) return;
    this.links.forEach((link) => {
      const active = decodeURIComponent(link.hash.slice(1)) === current.id;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('article-toc')) {
  customElements.define('article-toc', ArticleToc);
}
